import { useCallback, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Button, Card, Col, Flex, Row, Spin, Tooltip, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import Alert from 'components/Alert';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import { tap } from 'rxjs/operators';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import ConfirmTransactionMessage from 'v2/components/Shared/ConfirmTransactionMessage';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

const MIN_VALUE_TO_TRANSFER_DOLAR = 1000;

const CrossBorder = () => {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const currency = 'USD';
  const MinAbleToTransfer = MIN_VALUE_TO_TRANSFER_DOLAR;

  const [loading, setLoading] = useState(false);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const [data, error] = useObservable(() => financialService.listAvailable(user?.type).pipe(), []);
  const [bankAccounts, bankAccountsError] = useObservable(
    () => financialService.listBankAccounts({ active: 'true' }).pipe(),
    []
  );

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const totalToTransfer = useMemo(() => {
    return data?.origin_value || 0;
  }, [data]);

  const disableTransferButton = useMemo(() => {
    if (totalToTransfer < MinAbleToTransfer) {
      return true;
    }

    if (userHasBlockTransferBetaModule) {
      return true;
    }

    return false;
  }, [MinAbleToTransfer, totalToTransfer, userHasBlockTransferBetaModule]);

  const bankSelected = useMemo(() => (bankAccounts?.length ? bankAccounts[0] : null), [bankAccounts]);

  const isLoading = data === null && error === undefined;
  const isLoadingBankAccounts = bankAccounts === null && bankAccountsError === undefined;

  const [sendTransfer] = useCallbackObservable(() => {
    setLoading(true);
    return financialService
      .sendTransfer({ commissions: [], transfer_method_id: bankSelected.id, value: totalToTransfer })
      .pipe(
        tap(
          () => {
            toast.success('Sucesso, transferência enviada para processamento com êxito!');
            setLoading(false);
          },
          err => {
            setLoading(false);
            toast.error(err);
          }
        )
      );
  }, [totalToTransfer, bankSelected]);

  const makeTransfer = useCallback(async () => {
    const confirm = await Alert.confirm({
      message: <ConfirmTransactionMessage value={totalToTransfer} bankAccount={bankSelected} />,
      title: 'Atenção',
      buttonOk: 'Confirmar',
      confirmation: true
    });

    if (confirm) {
      sendTransfer();
    }
  }, [bankSelected, sendTransfer, totalToTransfer]);

  const handleSubmit = useCallback(() => {
    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    makeTransfer();
  }, [makeTransfer, shouldBlock]);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    makeTransfer();
  }, [makeTransfer, dismissBlock]);

  const renderTooltipValue = useMemo(() => {
    if (userHasBlockTransferBetaModule) {
      return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    }

    if (totalToTransfer < MinAbleToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={MinAbleToTransfer} format={currency}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [MinAbleToTransfer, currency, totalToTransfer, userHasBlockTransferBetaModule]);

  const renderTransferButton = useMemo(() => {
    if (isLoadingBankAccounts) return <div />;

    if (!bankSelected) {
      return <div>Você precisa de uma conta bancária cadastrada para transferir</div>;
    }

    if (user?.has_transfer) {
      return <div>Você tem transferências em processamento</div>;
    }

    if (bankAccountsError) {
      return <div>Erro ao buscar contas bancárias</div>;
    }

    return (
      <div className='flex items-center justify-between'>
        <Tooltip title={renderTooltipValue} placement='top'>
          <div>
            <Button
              type='primary'
              className='float-right max-sm:float-none'
              disabled={loading || disableTransferButton}
              onClick={handleSubmit}
              id='myTransferButton'
              icon={<Icon.DollarSign  />}
            >
              Transferir agora
            </Button>
          </div>
        </Tooltip>
      </div>
    );
  }, [
    bankAccountsError,
    bankSelected,
    disableTransferButton,
    handleSubmit,
    isLoadingBankAccounts,
    loading,
    renderTooltipValue,
    user
  ]);

  if (!user?.crossborder) return null;

  return (
    <div id='transfer-card-pj'>
      <Card className='h-full'>
        <Row>
          <Col xs={24} sm={6}>
            <Typography.Title level={5}>Carteira</Typography.Title>
            <Typography.Text type='secondary'>Cross Border</Typography.Text>
          </Col>

          <Col xs={24} sm={18} id='transfer-card-pj-values'>
            <Flex vertical align='end'>
              <Typography.Text strong>Disponível para Transferência:</Typography.Text>
              <Typography.Text type='secondary'>Valor total liberado para ser transferido</Typography.Text>
              <Typography.Title level={4} className='mb-0!'>
                {!isLoading ? (
                  <FormattedNumber format={currency} value={data?.origin_value || 0} />
                ) : (
                  <Spin size='small' />
                )}
              </Typography.Title>
            </Flex>
          </Col>
        </Row>

        <div className='mt-2' />

        <PermissionHide action={'myeduzz:financeiro:solicitar-transferencia'}>{renderTransferButton}</PermissionHide>
      </Card>
      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />
    </div>
  );
};

export default CrossBorder;
